// React imports
import { CloseOutlined, EditOutlined } from "@ant-design/icons"
import { Employee } from "@finway-group/shared/lib/models"
// Ant design imports
import { Alert, Button, Col, Modal, Row, Spin, Tag } from "antd"
import GroupedExpenses from "Components/groupedExpenses"
import Loading from "Components/loading"
import { EmployeeModal } from "Components/modals/employee.modal"
import EmployeeAbsenceModal from "Components/modals/employeeAbsence.modal"
import PageCustomHeader from "Components/PageCustomHeader"
import UserImage from "Components/UserImage"
import React, { Suspense, useEffect, useMemo } from "react"
import { Helmet } from "react-helmet"
import { useTranslation } from "react-i18next"
// Redux imports
import { useDispatch } from "react-redux"
import { useHistory } from "react-router"
import { useParams } from "react-router-dom"
import { useModal } from "Shared/context/modal.context"
import { useAreCardsActivated } from "Shared/hooks/corporate.hooks"
import { useEmployee, useEmployees } from "Shared/hooks/employee.hooks"
import { useIsFreeVersion } from "Shared/hooks/featureFlags.hooks"
// Import of shared modules
import { NotificationService, UserService } from "Shared/services"
import { NotificationTypeEnum } from "Shared/services/notification.service"
// Store imports
import { ThunkDispatchResult } from "Shared/store"
import { deleteEmployee, fetchOneEmployee } from "Shared/store/actions/employee/employeeActions"
import useStateIfMounted from "Shared/utils/hooks/useStateIfMounted"
import moment from "moment-timezone"
import Icon from "Components/icon"
import EmployeeTabView from "./employeeTabView"
import { useCompany } from "Shared/hooks/company.hooks"
import { formatEmployeeAbsenceDate } from "Features/pages/employees/employeeUtils"

const EmployeePublicProfile = React.lazy(() => import("./employeePublicProfile"))
const { confirm } = Modal

export const EmployeeDetail: React.FC<{}> = () => {
    const { t } = useTranslation()
    const { id } = useParams<{ id: string }>()
    const history = useHistory()
    const dispatch: ThunkDispatchResult = useDispatch()
    const { showModal } = useModal()
    const isFreeVersion = useIsFreeVersion()
    const { allowAllToUpdateAbsences } = useCompany()

    const employees = useEmployees(false, false, true)
    const employee = useEmployee()
    const loggedInUser = UserService.getLoggedInEmployeeProfile()
    const globalCurrency = loggedInUser.settings.globalCurrency

    const isMe = employee?.id === loggedInUser.id
    const isApproverFromSameTeam =
        loggedInUser.activeCompanyProfile.isApprover && employee.activeCompanyProfile?.team === loggedInUser.activeCompanyProfile.team && !employee.activeCompanyProfile?.isAdmin
    const canSee = (loggedInUser.activeCompanyProfile.isAdmin || isApproverFromSameTeam || isMe) && !isFreeVersion
    const canDeleteEmployee =
        loggedInUser.activeCompanyProfile.isAdmin ||
        (loggedInUser.activeCompanyProfile.isApprover && !employee.activeCompanyProfile?.isApprover && !employee.activeCompanyProfile?.isAdmin && isApproverFromSameTeam)
    const canUpdateEmployee = isMe || loggedInUser.activeCompanyProfile.isAdmin || (isApproverFromSameTeam && !employee.activeCompanyProfile?.isAdmin)
    const canUpdateAbsences = !isFreeVersion && (loggedInUser.activeCompanyProfile.isAdmin || (allowAllToUpdateAbsences && isMe))
    const cardsActive = useAreCardsActivated()
    const showPhoneNumber = loggedInUser.activeCompanyProfile.isAdmin || isMe

    const [isEmployeeAbsenceModalShowing, setIsEmployeeAbsenceModalShowing] = useStateIfMounted(false)

    const approvalList = useMemo(
        () =>
            (employee?.activeCompanyProfile?.approvals || []).map((approval) => ({
                ...approval,
                approver: employees.find((employee) => employee.id === approval.approver) || new Employee(),
            })),
        [employee]
    )

    useEffect(() => {
        dispatch(fetchOneEmployee(id)).catch((_error) => {
            history.push("/employees")
        })
    }, [id])

    const handleDeleteEmployee = async (employee: Employee) => {
        try {
            await dispatch(deleteEmployee(employee.id))
            NotificationService.send(NotificationTypeEnum.WARNING, t("notification:employee.deleted.title"), t("notification:employee.deleted.message"))
            history.push({ pathname: "/employees" })
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:employee.delete.title"))
        }
    }

    const handleDelete = () => {
        confirm({
            title: t("confirm:employee.delete.title"),
            content: t("confirm:employee.delete.message"),
            cancelText: t("confirm:employee.delete.cancel"),
            type: "warning",
            okText: t("confirm:employee.delete.confirm"),
            onOk: () => employee && handleDeleteEmployee(employee),
        })
    }

    const handleEditModal = () => showModal(EmployeeModal, false, { isShowing: true, employee, isNew: false })

    const isEmployeeAbsent = employee.isInAbsenceOfLeave()
    const daysLeft = employee.inAbsenceOfLeaveFor()
    const isEmployeeInFutureAbsent = employee.absence ? moment.tz(employee.timeZone).isBefore(employee.absence?.startDate) : false

    return (
        <Spin spinning={!employee}>
            {employee.id === id && (
                <div>
                    <Helmet>
                        <title>{employee.firstName + " " + employee.lastName}</title>
                    </Helmet>
                    {employee.activeCompanyProfile.deleted && (
                        <Alert
                            className="mb-20"
                            message={t("notification:employee.already_deleted.title")}
                            type="error"
                            description={t("notification:employee.already_deleted.message")}
                            closable={false}
                            showIcon={true}
                        />
                    )}
                    {isEmployeeAbsenceModalShowing && (
                        <EmployeeAbsenceModal
                            isVisible={isEmployeeAbsenceModalShowing}
                            onCancel={() => setIsEmployeeAbsenceModalShowing(!isEmployeeAbsenceModalShowing)}
                            employee={employee}
                        />
                    )}
                    <PageCustomHeader
                        title={
                            <div className="flex">
                                <div>
                                    <UserImage user={employee} size={55} />
                                </div>
                                <div className="flex justify-center flex-col truncate">
                                    <h1 className="mb-3 truncate">
                                        {employee.firstName} {employee.lastName}{" "}
                                        {employee.settings && employee.settings.security?.tfaEnabled && <Tag className="ant-tag-blue mt-4 ml-4">2fa</Tag>}
                                    </h1>
                                    <p className="leading-tight truncate">
                                        {employee.position}, {employee.location}
                                    </p>
                                </div>
                            </div>
                        }
                        actionButtons={
                            !employee.activeCompanyProfile.deleted && (
                                <>
                                    <div className="flex-wrap btn-wrapper mt-5">
                                        {isEmployeeAbsent && (
                                            <Button className="pointer-events-none bg-white" icon={<Icon className="pb-2 pr-6" color="#fff" icon="calendar_cancel" />}>
                                                {t("label:employee_absence.back_in")}{" "}
                                                <span className="font-medium">&nbsp;{t("label:employee_absence.day", { count: daysLeft })} </span>
                                            </Button>
                                        )}
                                        {isEmployeeInFutureAbsent && (
                                            <Button className="pointer-events-none bg-white" icon={<Icon className="pb-2 pr-6" color="#fff" icon="calendar_cancel" />}>
                                                {formatEmployeeAbsenceDate(employee)}
                                            </Button>
                                        )}
                                        {canDeleteEmployee && (
                                            <Button className="btn-default" onClick={handleDelete} icon={<CloseOutlined />}>
                                                <span>{t("action:delete")}</span>
                                            </Button>
                                        )}
                                        {canUpdateEmployee && (
                                            <Button className="btn-default" onClick={handleEditModal} icon={<EditOutlined />}>
                                                <span>{t("action:edit")}</span>
                                            </Button>
                                        )}
                                        {canUpdateAbsences && (
                                            <Button
                                                icon={<Icon className="pr-6" color="#fff" icon="calendar_absence" />}
                                                onClick={() => setIsEmployeeAbsenceModalShowing(!isEmployeeAbsenceModalShowing)}
                                                type="primary"
                                            >
                                                {t("action:employee_absence.absence")}
                                            </Button>
                                        )}
                                    </div>
                                </>
                            )
                        }
                    />
                    <Row gutter={[16, 16]}>
                        <Col xs={24} md={14}>
                            <div id="employee-account-details-section">
                                <Suspense fallback={<Loading />}>
                                    <EmployeePublicProfile employee={employee} loggedInUser={loggedInUser} showPhoneNumber={showPhoneNumber} />
                                </Suspense>
                            </div>

                            {canSee && (
                                <div className="mt-24" id="employee-expenses-section">
                                    <GroupedExpenses groupedByField="requestedBy" groupedById={employee.id} />
                                </div>
                            )}
                        </Col>
                        <Col xs={24} md={10}>
                            {canSee && (
                                <div className="mt-24 md:mt-0" id="employee-limits-section">
                                    <EmployeeTabView globalCurrency={globalCurrency} approvals={approvalList} employee={employee} cardsActive={cardsActive} />
                                </div>
                            )}
                        </Col>
                    </Row>
                </div>
            )}
        </Spin>
    )
}

export default EmployeeDetail
